// src/pages/feedback/components/FeedbackRow.tsx
//
// 목록의 한 행. 데스크톱(md+)은 그리드 행, 모바일은 같은 데이터를 카드로
// 재배치한다 — 컴포넌트는 하나이고 레이아웃만 갈린다.
//
// DataGridPro 를 쓰지 않은 이유(기록): ① 게시판 행은 "제목 + 칩 2종 +
// 카운터"라 셀 정렬보다 타이포 위계가 중요하고, ② 모바일에서 같은 행을
// 카드로 재배치해야 하는데 DataGrid 는 컬럼 축소만 되며, ③ 정렬·페이지를
// 서버가 담당해 그리드의 클라이언트 기능이 무의미하다. 대신 프레임
// (LedgerFrame)과 행 구분선 색(#EDEFF1)을 DataGrid 오버라이드와 같은 값으로
// 맞춰 "표와 같은 인상"을 유지한다.
//
// 호버는 배경색만 바뀐다 — translate 호버는 디자인 시스템에서 제거된 언어다.

import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";

import { designTokens } from "../../../theme.ts";
import { fmtDate } from "../../../utils/dateFormat.ts";
import {
  authorLabel,
  FeedbackPostDto,
} from "../../../contracts/feedback/types.ts";
import FeedbackStatusChip from "./FeedbackStatusChip.tsx";
import FeedbackTypeChip from "./FeedbackTypeChip.tsx";

const T = designTokens;

const C = {
  ink: "#171A1E",
  sub: "#6B7178",
  caption: "#9AA0A6",
  rowLine: "#EDEFF1",
  hover: "#F2F4F5",
  // 포커스 링은 액센트 **원색**이다. 틴트(#FDEDE8)로 그리면 같은 규칙이
  // 동시에 칠하는 호버 배경(#F2F4F5)과 대비가 1.03:1 밖에 안 나와
  // WCAG 2.4.11(3:1)을 못 맞추고, 키보드 사용자가 현재 행을 알 수 없다.
  // RentalCard.tsx 가 포커스에 C.accent 원색을 쓰는 것과 같은 처리.
  focusRing: T.cargo,
};

interface FeedbackRowProps {
  post: FeedbackPostDto;
  onOpen: (id: string) => void;
  /** 마지막 행의 아래 구분선을 없앤다. */
  isLast?: boolean;
}

/** 댓글 수 — 0이면 아이콘·숫자 모두 렌더하지 않는다(잡음 제거). */
function CommentCount({ count }: { count: number }) {
  if (!count) return null;
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.4,
        color: C.sub,
        fontFamily: T.fonts.sans,
        fontSize: 12.5,
        fontVariantNumeric: "tabular-nums",
        whiteSpace: "nowrap",
      }}
      aria-label={`댓글 ${count}개`}
    >
      <ChatBubbleOutlineIcon sx={{ fontSize: 14 }} />
      {count}
    </Box>
  );
}

export default function FeedbackRow({ post, onOpen, isLast = false }: FeedbackRowProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const interaction = {
    cursor: "pointer",
    outline: "none",
    borderBottom: isLast ? "none" : `1px solid ${C.rowLine}`,
    transition: "background-color 140ms ease",
    "&:hover": { bgcolor: C.hover },
    "&:focus-visible": {
      bgcolor: C.hover,
      boxShadow: `inset 0 0 0 2px ${C.focusRing}`,
    },
  } as const;

  const rowProps = {
    role: "button",
    tabIndex: 0,
    onClick: () => onOpen(post.id),
    onKeyDown: (e: React.KeyboardEvent) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onOpen(post.id);
      }
    },
  };

  if (isMobile) {
    return (
      <Box
        {...rowProps}
        sx={{
          ...interaction,
          px: 2,
          py: 1.75,
          minHeight: 72, // 터치 타깃
          display: "flex",
          flexDirection: "column",
          gap: 0.75,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          <FeedbackTypeChip type={post.category} />
          <FeedbackStatusChip status={post.status} />
          <Box sx={{ flex: 1 }} />
          <CommentCount count={post.commentCount} />
        </Box>

        <Typography
          sx={{
            fontFamily: T.fonts.sans,
            fontSize: 14.5,
            fontWeight: 600,
            color: C.ink,
            lineHeight: 1.45,
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            overflowWrap: "anywhere",
            wordBreak: "break-word",
          }}
        >
          {post.title}
        </Typography>

        <Box
          sx={{
            fontFamily: T.fonts.sans,
            fontSize: 12.5,
            color: C.caption,
            fontVariantNumeric: "tabular-nums",
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {authorLabel(post)} · {fmtDate(post.createdTimestamp)}
        </Box>
      </Box>
    );
  }

  return (
    <Box
      {...rowProps}
      sx={{
        ...interaction,
        px: 2,
        py: 1.5,
        display: "grid",
        gridTemplateColumns: "72px minmax(0,1fr) 88px 104px 76px 52px",
        alignItems: "center",
        gap: 1.25,
      }}
    >
      <FeedbackTypeChip type={post.category} />

      <Typography
        noWrap
        sx={{
          fontFamily: T.fonts.sans,
          fontSize: 14.5,
          fontWeight: 600,
          color: C.ink,
          minWidth: 0,
          textOverflow: "ellipsis",
        }}
      >
        {post.title}
      </Typography>

      <FeedbackStatusChip status={post.status} />

      <Typography
        noWrap
        sx={{
          fontFamily: T.fonts.sans,
          fontSize: 13,
          color: C.sub,
          minWidth: 0,
          textOverflow: "ellipsis",
        }}
      >
        {authorLabel(post)}
      </Typography>

      <Box
        sx={{
          fontFamily: T.fonts.sans,
          fontSize: 12.5,
          color: C.caption,
          fontVariantNumeric: "tabular-nums",
          whiteSpace: "nowrap",
        }}
      >
        {fmtDate(post.createdTimestamp)}
      </Box>

      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <CommentCount count={post.commentCount} />
      </Box>
    </Box>
  );
}
